// frontend/src/components/GlobalNav.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import './GlobalNav.css';
import { useAdmin } from 'context/AdminContext';
import { SUPPORT_EMAIL } from '../config';

const routes = [
  { path: '/', label: 'Home' },
  { path: '/store', label: 'Store' },
  { path: '/chat', label: 'Chat' },
  { path: '/assistant', label: 'Full Assistant' },
  { path: '/script-lab', label: 'Script Lab' },
  { path: '/heightmap', label: 'Heightmap' },
  { path: '/surface', label: 'Surface' },
  { path: '/blog', label: 'Blog' },
  { path: '/portfolio', label: 'Portfolio' },
  { path: '/help', label: 'Help' }
];

const funeralHomeRoutes = [
  { path: '/memorial', label: 'Family Memorial Page' },
  {
    href: `mailto:${SUPPORT_EMAIL}?subject=Funeral Home Information Request`,
    label: 'Request Info'
  }
];

const memorialRoutes = [
  { path: '/funeral-homes', label: 'Funeral Home Info' },
  {
    href: `mailto:${SUPPORT_EMAIL}?subject=Memorial Keepsake Information Request`,
    label: 'Request Info'
  }
];

const GlobalNav = ({ onLogout, member, onMemberLogout }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { adminStatus, isAdmin } = useAdmin();

  const toggle = () => setOpen(v => !v);
  const close = () => setOpen(false);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isFuneralHomeRoute = ['/funeral-homes', '/funeral-home', '/funeralhome'].includes(location.pathname);
  const isMemorialRoute = location.pathname === '/memorial';
  const usesMemorialPublicNav = isFuneralHomeRoute || isMemorialRoute;
  const adminChecking = adminStatus === 'checking' || adminStatus === 'unknown';
  const visibleRoutes = isFuneralHomeRoute
    ? funeralHomeRoutes
    : isMemorialRoute
      ? memorialRoutes
      : routes;

  return (
    <div className="hf-global-nav">
      <button
        className="hf-nav-toggle"
        onClick={toggle}
        aria-expanded={open}
        aria-label="Toggle navigation"
      >
        <span className="hf-nav-toggle-dot" />
        <span className="hf-nav-toggle-label">Menu</span>
      </button>

      {open && (
        <div className="hf-nav-panel">
          <div className="hf-nav-panel-header">
            <div className="hf-nav-brand">
              <span className="hf-nav-logo">☠</span>
              <span className="hf-nav-title">HexForge Labs</span>
            </div>
            <button className="hf-nav-close" onClick={close}>
              ✕
            </button>
          </div>

          <div className="hf-nav-links">
            {visibleRoutes.map(r => {
              const active = location.pathname === r.path;
              if (r.href) {
                return (
                  <a key={r.href} href={r.href} onClick={close} className="hf-nav-link">
                    {r.label}
                  </a>
                );
              }

              return (
                <Link
                  key={r.path}
                  to={r.path}
                  onClick={close}
                  className={
                    'hf-nav-link' + (active ? ' hf-nav-link--active' : '')
                  }
                >
                  {r.label}
                </Link>
              );
            })}

            {!usesMemorialPublicNav && (
              <>
                {/* Member account controls */}
                {member ? (
                  <>
                    <Link
                      to="/account"
                      onClick={close}
                      className={
                        'hf-nav-link' +
                        (location.pathname === '/account'
                          ? ' hf-nav-link--active'
                          : '')
                      }
                    >
                      Account ({member.username})
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        onMemberLogout?.();
                        close();
                      }}
                      className="hf-nav-link hf-nav-link--danger"
                    >
                      🔒 Log Out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={close}
                    className={
                      'hf-nav-link' +
                      (location.pathname === '/login'
                        ? ' hf-nav-link--active'
                        : '')
                    }
                  >
                    Sign In
                  </Link>
                )}

                {/* Admin controls (global) */}
                {isAdmin ? (
                  <>
                    <Link
                      to="/admin"
                      onClick={close}
                      className={
                        'hf-nav-link' +
                        (isAdminRoute ? ' hf-nav-link--active' : '')
                      }
                    >
                      Admin
                    </Link>
                    {onLogout && (
                      <button
                        type="button"
                        onClick={() => {
                          onLogout();
                          close();
                        }}
                        className="hf-nav-link hf-nav-link--danger"
                      >
                        🧱 Admin Log Out
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to="/admin-login"
                    onClick={close}
                    className={
                      'hf-nav-link' +
                      (location.pathname === '/admin-login'
                        ? ' hf-nav-link--active'
                        : '')
                    }
                  >
                    {adminChecking ? 'Admin (checking...)' : 'Admin Sign In'}
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

GlobalNav.propTypes = {
  onLogout: PropTypes.func,
  member: PropTypes.shape({
    username: PropTypes.string,
    email: PropTypes.string
  }),
  onMemberLogout: PropTypes.func
};

export default GlobalNav;
